<template>
  <div
    v-if="loaded"
    class="study-request-form"
  >
    <vt-side-panel :visible="toggleSidepanel">
      <div class="px-3">
        <b-row class="px-3 mb-3">
          <b-col class="d-flex align-items-center">
            <span
              v-if="attachmentsSidepanel"
              class="ap-title ap-title__main"
            >{{ $tc('global.file', 2) }}</span>
            <span
              v-if="incidentsSidepanel"
              class="title"
            >{{ $tc('global.incident', 2) }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div>
              <b-button
                v-shortkey="['shift', 'esc']"
                class="btn__icon btn__icon--primary p-0"
                @shortkey="toggleSidepanel = false"
                @click="toggleSidepanel = false"
              >
                <i
                  class="far fa-times"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <div
          v-if="requestId"
          class="content-scroll"
        >
          <b-col
            v-if="attachmentsSidepanel"
            class="mt-2"
          >
            <request-downloadable-attachment
              :is-formalized="request.status && !$_isRequestDraft(request)"
              :attachments="attachments"
              :user-have-attach-operation="userHaveAttachOperation"
              :request-id="requestId"
              :lab-id="labId"
              @deleteAttachment="onDeleteAttachment"
            />
          </b-col>
          <b-col
            v-if="incidentsSidepanel && incidentsList.length"
            class="mt-2"
          >
            <request-incident-card
              v-for="incident in incidentsList"
              :key="incident.incidentId"
              :incident="incident"
            />
          </b-col>
        </div>
      </div>
    </vt-side-panel>
    <b-row class="search-block mb-3 mt-1">
      <b-col
        xl="8"
        lg="10"
        md="10"
        class="d-flex align-items-center"
      >
        <div v-if="showBackButton">
          <b-button
            class="pr-3 pl-2 btn__icon btn__icon--primary"
            variant="primary"
            @click="returnToPreviousView"
          >
            <i
              v-tooltip="$t('global.goBack')"
              class="far fa-arrow-left"
              aria-hidden="true"
            />
          </b-button>
        </div>
        <searcher-module-entity-selection
          ref="localSearcherClinical"
          :identifier-options="identifierScopesForSearch"
          :initial-identifier="identifierScopesForSearch[0]"
          @requestSearch="requestSearch"
          @studySearch="studySearch"
          @patientSearch="patientSearch"
        />
      </b-col>
      <b-col
        xl="4"
        lg="2"
        md="2"
        class="text-right"
      >
        <div
          class="d-flex align-items-center justify-content-end mt-1"
          style="margin-bottom: -1px;"
        >
          <b-button
            v-if="$_canCreateRequest(labId)"
            id="requestsRouteToStudyRequestCreate"
            qa="clinical-grid-create-request-button"
            class="ml-2 btn btn--primary btn-secondary btn-sm"
            @click="$refs.clinicalSearchPatientModal.show()"
          >
            <fa-icon
              class="mr-1"
              :icon="['fal', 'plus']"
            />
            {{ $tc('global.request', 1) }}
          </b-button>
        </div>
      </b-col>
    </b-row>
    <patient-card
      v-if="patientInformation"
      :patient="patientInformation"
      @openPatientModal="$refs.modalPatientCard.show()"
    >
      <b-button
        v-if="$_canCreateRequest(labId)"
        slot="card-buttons"
        qa="patient-card-reassign-patient"
        :disabled="request.status && !$_isRequestDraft(request)"
        class="pr-0 py-0 btn__icon btn__icon--primary"
        variant="primary"
        @click="reassignPatient"
      >
        <i
          v-tooltip="$t('global.reassignPatient')"
          class="far fa-user-edit"
          aria-hidden="true"
        />
      </b-button>
    </patient-card>
    <b-card class="float-component flex-grow-1 mt-3 pt-3 mb-5">
      <b-row class="px-3">
        <b-col class="d-flex align-items-center">
          <div class="d-flex align-items-md-center align-items-sm-start flex-sm-column flex-md-row">
            <div class="d-flex align-items-center">
              <span class="ap-title ap-title__main m-0 mr-1 d-flex align-items-center">
                <b-input-group
                  v-if="request.isExternalLabel && (!request.status || $_isRequestDraft(request))"
                  size="sm"
                  class="printed-label"
                  :class="{ requiredform: (showErrors && !isRequestExternalLabelValid) }"
                >
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i
                        class="main far fa-file-medical"
                        aria-hidden="true"
                      />
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <vt-input
                    v-model="request.label"
                    v-focus
                    v-validate="'required'"
                    qa="study-request-form-request-input"
                    :class="{ requiredform: (showErrors && request.isExternalLabel && !isRequestExternalLabelValid) }"
                    :placeholder="$t('global.enterIdentifier') + ' *'"
                  />
                </b-input-group>
                <template v-else>
                  <i
                    class="far fa-file-medical mr-3"
                    aria-hidden="true"
                  />
                  <span qa="study-request-form-request-label">{{ request.label ? request.label : $t('global.newRequest') }}</span>
                </template>
              </span>
              <b-badge
                v-if="request.automaticallyCreated"
                v-tooltip="$t('global.automaticCreation')"
                pill
                class="mx-2 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
              >
                <i
                  class="far fa-magic"
                  aria-hidden="true"
                />
              </b-badge>
              <vtBubble
                v-show="incidents"
                :type="true"
                :number="incidents"
                :tooltip="` ${$tc('grid.detail.hasAlarms')}`"
                @click="showIncidentsSidepanel()"
              />
              <vtBubble
                v-show="attachments.length"
                class="mr-2"
                :type="false"
                :tooltip="` ${$tc('global.file', 2)}`"
                :number="attachments.length"
                @click="showAttachments()"
              />
              <study-request-status-badge
                v-if="requestId"
                class="ml-2"
                :status="request.status"
              />
            </div>
            <div
              v-if="request.reqRequestDate"
              v-tooltip="$t('grid.detail.casRequestDate')"
              class="ml-md-4 draft-date dark"
            >
              <small><i
                class="far fa-calendar mr-2"
                aria-hidden="true"
              />{{ request.reqRequestDate | datetime }}</small>
            </div>
          </div>
        </b-col>
        <b-col class="d-flex align-items-center justify-content-end">
          <div class="switch-btn p-0 d-flex align-items-center mr-4">
            <span class="mr-2 printed-label-switch-title">{{ $t('global.scanPrintedLabel') }}</span>
            <input
              id="toggle-clinical-label"
              v-model="request.isExternalLabel"
              :disabled="request.status && !$_isRequestDraft(request)"
              class="offscreen"
              type="checkbox"
              :value="request.isExternalLabel"
              aria-hidden="true"
              @change="saveSwitchExternalLabelState"
            >
            <label
              qa="study-request-form-print-label-switch-label"
              for="toggle-clinical-label"
              class="switch mb-0"
            />
          </div>
          <b-button
            v-if="userHaveAttachOperation"
            id="request-pop"
            :disabled="request.status && !$_isRequestDraft(request)"
            qa="study-request-form-attach-button"
            class="btn__icon btn__icon--primary mx-1 px-2"
            @click="openAttachModal"
          >
            <i
              v-tooltip="$t('hoverBtn.attach')"
              class="far fa-paperclip"
              aria-hidden="true"
            />
          </b-button>
          <vt-loading-button
            v-if="$_canPrintRequestReport(labId)"
            slot="buttons-end"
            qa="study-request-form-request-report-button"
            class="btn__icon btn__icon--primary mx-1 px-2"
            :disabled="disabledPrintRequestReport"
            :params="{ promise: downloadRequestReportPdf }"
          >
            <i
              v-tooltip="$t('clinical.grid.viewRequestReport')"
              class="far fa-file-medical"
              aria-hidden="true"
            />
          </vt-loading-button>
          <vt-loading-button
            v-if="$_canPrintRequestReport(labId)"
            id="requestFormRequest"
            slot="buttons-end"
            qa="study-request-form-print-button"
            class="btn__icon btn__icon--primary mx-1 px-2"
            :disabled="disabledPrintRequestReport"
            :params="{ promise: printRequestReport }"
          >
            <i
              v-tooltip="$t('clinical.btn.printRequest')"
              class="far fa-print"
              aria-hidden="true"
            />
          </vt-loading-button>
        </b-col>
      </b-row>
      <div class="scrollable-area">
        <b-row class="mt-3 px-3">
          <b-col>
            <alert-banner
              :show="request.isExternalLabel && (!request.status || $_isRequestDraft(request))"
              variant="warning"
              close
              :text="$tc('clinical.alertBanner.printedLabel')"
            />
            <div class="form-card">
              <span class="ap-title ap-title__section">
                <i
                  class="far fa-list mr-2"
                  aria-hidden="true"
                />{{ $t('global.requestData') }}
              </span>
              <b-row class="mt-2">
                <b-col
                  sm="6"
                  md="4"
                  lg="3"
                  xl="2"
                  class="mb-sm-3"
                >
                  <div class="form-label">
                    <label :class="{ requiredlabel: (showErrors && !request.laboratory) }">
                      {{ $tc('component.studyRequest.form.petitionCard.laboratory') }} *
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="request.laboratory"
                    v-validate="'required'"
                    :disabled="!!requestId || !laboratories.length"
                    qa="study-request-form-laboratory-select"
                    :class="{ requiredform: (showErrors && !request.laboratory) }"
                    focus
                    :options="laboratories"
                    track-by="labId"
                    label="labName"
                    :allow-empty="false"
                    @input="resetLaboratoryChanges"
                  />
                </b-col>
                <b-col
                  sm="6"
                  md="4"
                  lg="2"
                  class="mb-sm-3"
                  :class="{'col-sm-1': htmlShowCaseLabelInput, 'col-xl-1': htmlShowCaseLabelInput, 'col-xl-2': !htmlShowCaseLabelInput}"
                >
                  <div class="form-label">
                    <label :class="{ requiredlabel: (showErrors && !request.priority) }">
                      {{ $t('component.studyRequest.form.petitionCard.priority') }} *
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="request.priority"
                    v-validate="'required'"
                    :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                    qa="study-request-form-priority-select"
                    :class="{ requiredform: (showErrors && !request.priority) }"
                    :options="priorities"
                    track-by="id"
                    label="name"
                    :allow-empty="false"
                  />
                </b-col>
                <b-col
                  sm="6"
                  md="4"
                  lg="3"
                  class="mb-sm-3"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('component.studyRequest.form.petitionCard.regime') }}
                    </label>
                  </div>
                  <vt-combo-select
                    v-model="request.regime"
                    :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                    qa="study-request-form-regime-select"
                    track-by="id"
                    :option-loader="loadRegimes"
                    label="name"
                  />
                </b-col>
                <b-col
                  sm="6"
                  md="4"
                  lg="2"
                  class="mb-sm-3"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('component.studyRequest.form.petitionCard.episode') }}
                    </label>
                  </div>
                  <vt-input
                    v-model="request.reqEpisode"
                    :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                    qa="study-request-form-episode-input"
                    class="form-control-sm"
                    :name="$t('component.studyRequest.form.petitionCard.episode')"
                  />
                </b-col>
                <b-col
                  v-if="htmlShowCaseLabelInput"
                  sm="6"
                  md="4"
                  lg="2"
                  class="mb-sm-3"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('component.caseCardModal.studyId') }}
                    </label>
                  </div>
                  <vt-input
                    v-model="request.caseLabel"
                    :disabled="htmlDisabledCaseLabelInput"
                    qa="study-request-form-episode-input"
                    class="form-control-sm"
                    :name="$t('component.caseCardModal.studyId')"
                  />
                </b-col>
                <b-col
                  sm="6"
                  md="4"
                  lg="2"
                  class="mb-sm-3"
                >
                  <div class="form-label">
                    <label>
                      {{ $t('global.externalReference') }}
                    </label>
                  </div>
                  <vt-input
                    v-model="request.externalReference"
                    :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                    qa="study-request-form-external-reference-input"
                    class="form-control-sm"
                    :name="$t('global.externalReference')"
                  />
                </b-col>
                <b-col
                  md="7"
                  xl="6"
                  :class="{ 'col-lg-5':htmlShowCaseLabelInput, 'col-lg-7':!htmlShowCaseLabelInput }"
                  class="mb-lg-0"
                >
                  <b-form-group :label="$tc('component.studyRequest.form.petitionCard.clinicalData')">
                    <b-form-textarea
                      v-model="request.clinicalData.text"
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      qa="study-request-form-clinical-data-input"
                      class="text-box"
                      :rows="2"
                      :max-rows="2"
                    />
                  </b-form-group>
                </b-col>
                <b-col
                  md="5"
                  lg="5"
                  xl="6"
                  class="mb-sm-2 mb-lg-0"
                >
                  <b-form-group :label="$tc('global.observations')">
                    <b-form-textarea
                      v-model="request.notes.text"
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      qa="study-request-observations-input"
                      class="text-box"
                      :rows="2"
                      :max-rows="2"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  lg="12"
                  xl="6"
                >
                  <b-row>
                    <b-col
                      cols="auto"
                      class="d-flex align-items-center"
                    >
                      <span class="ap-title ap-title__divider">{{ $t('global.requester') }}</span>
                      <b-form-checkbox
                        v-if="!requestId"
                        id="check-destination"
                        v-model="differentDestination"
                        qa="study-request-form-different-destination-checkbox"
                        class="ml-3"
                        name="check-destination"
                        :value="true"
                        :unchecked-value="false"
                      />
                      <small v-if="!requestId">{{ $t('registry.cases.differentDestination') }}</small>
                    </b-col>
                    <b-col>
                      <hr>
                    </b-col>
                  </b-row>
                  <b-row class="mt-0 mb-2">
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('component.studyRequest.form.clinicCard.physicianCenter') }}
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="selectedPhysicianCenter"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        qa="study-request-form-physician-center"
                        track-by="id"
                        :option-loader="loadCenters"
                        label="name"
                        @input="selectPhysicianCenter"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('component.studyRequest.form.clinicCard.physicianDepartment') }}
                        </label>
                      </div>
                      <vt-combo-select
                        id="physicianDepartment"
                        :key="selectedPhysicianCenterId"
                        v-model="request.physicianDepartment"
                        qa="study-request-form-physician-department"
                        track-by="id"
                        :option-loader="loadPhysicianDepartments"
                        label="name"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        @input="selectPhysicianDepartment"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label :class="{ requiredlabel: (showErrors && !request.physicianPetitioner) }">
                          {{ $t('component.studyRequest.form.clinicCard.petitioner') }} *
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="request.physicianPetitioner"
                        v-validate="'required'"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        qa="study-request-form-physician-petitioner"
                        :open-direction="'top'"
                        track-by="id"
                        :allow-empty="false"
                        :option-loader="loadPetitioners"
                        ajax-search
                        :class="{ requiredform: (showErrors && !request.physicianPetitioner) }"
                        label="fullName"
                        :internal-search="false"
                        @input="customLabelProfessional($event)"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  lg="12"
                  xl="6"
                >
                  <b-row
                    class="pt-0"
                  >
                    <b-col
                      cols="auto"
                      class="d-flex align-items-center"
                    >
                      <span class="ap-title ap-title__divider">{{ $tc('global.insurance', 1) }}</span>
                    </b-col>
                    <b-col>
                      <hr>
                    </b-col>
                  </b-row>
                  <b-row
                    class="mt-0 mb-2"
                  >
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $tc('global.insurance', 1) }}
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="request.insurer"
                        qa="study-request-form-insurance-select"
                        label="name"
                        :disabled="hasRequestIdAndIsNotInDraft"
                        :option-loader="findAllActiveInsurers"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $tc('global.insuranceDocument') }}
                        </label>
                      </div>
                      <vt-input
                        v-model="request.referralNumber"
                        :disabled="hasRequestIdAndIsNotInDraft"
                        size="sm"
                        qa="study-request-form-insurance-document-input"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('global.insurancePolicy') }}
                        </label>
                      </div>
                      <vt-input
                        v-model="request.insuranceNumber"
                        :disabled="hasRequestIdAndIsNotInDraft"
                        size="sm"
                        qa="study-request-form-insurance-policy-input"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  lg="12"
                  xl="6"
                >
                  <b-row
                    v-if="differentDestination || requestId"
                    class="pt-0"
                  >
                    <b-col
                      cols="auto"
                      class="d-flex align-items-center"
                    >
                      <span class="ap-title ap-title__divider">{{ $t('registry.cases.destination') }}</span>
                    </b-col>
                    <b-col>
                      <hr>
                    </b-col>
                  </b-row>
                  <b-row
                    v-if="differentDestination || requestId"
                    class="mt-0 mb-2"
                  >
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('component.studyRequest.form.clinicCard.destinationCenter') }}
                        </label>
                      </div>
                      <vt-combo-select
                        v-model="selectedDestinationCenter"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        track-by="id"
                        qa="study-request-form-destination-center-select"
                        :option-loader="loadCenters"
                        label="name"
                        @input="selectDestinationCenter"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('component.studyRequest.form.clinicCard.destinationDepartment') }}
                        </label>
                      </div>
                      <vt-combo-select
                        id="destinationDepartment"
                        :key="selectedDestinationCenterId"
                        v-model="request.destinationDepartment"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        qa="study-request-form-episode-destination-department-select"
                        track-by="id"
                        :option-loader="loadDestinationDepartments"
                        label="name"
                        @input="selectDestinationDepartment"
                      />
                    </b-col>
                    <b-col
                      sm="6"
                      md="4"
                      class="mb-sm-3"
                    >
                      <div class="form-label">
                        <label>
                          {{ $t('component.studyRequest.form.clinicCard.destinationPhysicianName') }}
                        </label>
                      </div>
                      <vt-input
                        v-model="request.reqAttn"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        qa="study-request-form-destination-petitioner-input"
                        class="form-control-sm"
                        name="destinationPet"
                      />
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>
        <b-row class="my-3 px-3">
          <b-col>
            <div class="form-card">
              <div class="d-flex align-items-center justify-content-between">
                <span class="ap-title ap-title__section">
                  <i
                    class="far fa-prescription-bottle mr-2"
                    aria-hidden="true"
                  />{{ $tc('global.sample', 2) }}
                </span>
                <div class="d-flex">
                  <b-button
                    v-if="$_canPrintRequestReport(labId)"
                    :disabled="!request.status"
                    qa="study-request-form-destination-print-labels-button"
                    class="p-0 mt-1 btn__icon btn__icon--primary"
                    tabindex="-1"
                    variant="primary"
                    @click="printLabels(false)"
                  >
                    <i
                      v-tooltip="$t('component.studyRequest.subRequestForm.printLabels')"
                      class="far fa-ticket-alt px-2"
                      aria-hidden="true"
                    />
                  </b-button>
                  <span
                    id="requestFormSubRequestPrintLabelPopover"
                    class="d-flex align-items-center"
                  >
                    <b-button
                      v-if="$_canPrintRequestReport(labId)"
                      :disabled="!request.status"
                      qa="study-request-form-destination-print-custom-labels-button"
                      tabindex="-1"
                      class="p-0 mt-1 btn__icon btn__icon--primary"
                      variant="primary"
                    >
                      <i
                        v-tooltip="$t('component.studyRequest.subRequestForm.printCustomLabels')"
                        class="icon-ticket-setup px-2"
                        aria-hidden="true"
                      />
                    </b-button>
                  </span>
                  <print-label-popover
                    v-if="$_canPrintRequestReport(labId)"
                    :disabled="!requestId || !request.status"
                    target="requestFormSubRequestPrintLabelPopover"
                    label-field="surExternalId"
                    :elements="request.subrequests"
                    :lab-id="labId"
                    :parent-id="requestId"
                    @printSelectedLabelsWithCopies="printSelectedLabelsWithCopies"
                  />
                  <b-button
                    :disabled="request.status && !$_isRequestDraft(request)"
                    qa="study-request-form-destination-remove-all-samples-button"
                    tabindex="-1"
                    class="pl-2 btn__icon btn__icon--primary"
                    variant="primary"
                    @click="deleteSubRequest(true)"
                  >
                    <i
                      v-tooltip="$t('component.studyRequest.subRequestForm.removeAll')"
                      class="far fa-trash-alt"
                      aria-hidden="true"
                    />
                  </b-button>
                  <div class="d-flex align-items-center ml-3">
                    <vt-input
                      v-model="subrequestNumberToAdd"
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      type="number"
                      qa="study-request-form-destination-add-sample-input"
                      class="form-control-sm"
                      placeholder="1"
                      style="width:60px"
                    />
                    <b-button
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      qa="study-request-form-destination-add-sample-button"
                      class="btn--primary btn-sm ml-1 d-flex align-items-center"
                      @click="addSubRequest"
                    >
                      <i
                        class="fal fa-plus mr-2"
                        aria-hidden="true"
                      />{{ $tc('global.sample', 1) }}
                    </b-button>
                  </div>
                </div>
              </div>
              <b-row>
                <b-col>
                  <div style="position: relative;">
                    <div class="tab-buttons mt-3 d-flex">
                      <b-button
                        v-if="$_canPrintRequestReport(labId)"
                        class="btn__icon btn__icon--primary mx-2"
                        :disabled="!request.status"
                        tabindex="-1"
                        @click="printLabels(true)"
                      >
                        <i
                          v-tooltip="$t('component.studyRequest.subRequestForm.printLabel')"
                          aria-hidden="true"
                          class="far fa-ticket-alt"
                        />
                      </b-button>
                      <b-button
                        class="btn__icon btn__icon--primary mx-2"
                        :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                        tabindex="-1"
                        @click="deleteSubRequest(false)"
                      >
                        <i
                          v-tooltip="$t('component.studyRequest.subRequestForm.remove')"
                          aria-hidden="true"
                          class="far fa-trash-alt"
                        />
                      </b-button>
                    </div>
                    <b-tabs
                      ref="subRequestsTabs"
                      v-model="selectedSubrequestIndex"
                      v-shortkey="{left: ['ctrl','arrowleft'], right: ['ctrl','arrowright']}"
                      tabindex="-1"
                      class="vt-tabs samples-tabs"
                      @shortkey.native="handleSubrequestTabsNavigation"
                    >
                      <b-tab
                        v-for="(subrequest, index) in request.subrequests"
                        id="study-request-form-sample-tab"
                        :key="uniqueId(index)"
                        tabindex="-1"
                        class="pt-3"
                        :title="index | abc"
                        :title-link-class="{ errorTitle: (showErrors && (isEmpty(subrequest.topographicCode) || isEmpty(subrequest.sampleType) || (request.isExternalLabel && !subrequest.isLabelValid))) }"
                        @click="selectSubRequest(subrequest, index)"
                      />
                    </b-tabs>
                    <sub-request-form
                      v-if="labId && subRequestSelected"
                      ref="subRequestForm"
                      :key="subRequestFormKey"
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      :laboratory-id="labId"
                      :show-errors="showErrors"
                      :subrequest="subRequestSelected"
                      :selected-sample-types="filteredSampleTypes"
                      :is-external-label="request.isExternalLabel"
                    />
                    <sub-request-custom-form
                      ref="subRequestCustomForm"
                      :disabled="(request.status && !$_isRequestDraft(request)) || !$_canCreateRequest(labId)"
                      :laboratory-id="labId"
                      :subrequest="subRequestSelected"
                      @subrequestInformationsChanged="onSubrequestInformationsChanged"
                    />
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>
      </div>
      <div
        slot="footer"
        class="d-flex justify-content-between align-items-center"
      >
        <div>
          <vt-loading-button
            v-if="visibleDiscardRequestButton"
            qa="study-request-form-discard-button"
            class="btn__link btn__link--danger btn-sm mr-3"
            :params="{ promise: removeRequest, shortkeys: ['alt', 'del'] }"
          >
            {{ $t('component.studyRequest.form.discardDraft') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="visibleSaveAndFormalizeRequestButton"
            qa="study-request-form-formalize-button"
            class="btn__link btn-sm"
            :params="{ promise: saveAndFormalizeRequest, shortkeys: ['ctrl', 'enter'] }"
          >
            {{ $t('component.studyRequest.form.formalizeRequest') }}
          </vt-loading-button>
        </div>
        <div class="my-1 text-right">
          <vt-loading-button
            v-if="visibleCancelRequestButton"
            qa="study-request-form-cancel-button"
            class="btn__outline btn-sm ml-3"
            size="sm"
            :params="{ promise: cancelRequest }"
          >
            {{ $t('registry.requestGrid.contextMenu.cancelRequest') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="visibleSaveRequestButton"
            qa="study-request-form-save-button"
            class="btn__outline btn-sm ml-3"
            :params="{ promise: saveRequest, shortkeys: ['alt', 's'] }"
          >
            <i
              class="fal fa-save mr-2"
              aria-hidden="true"
            />{{ $t('component.studyRequest.form.saveChanges') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="visibleSaveFormalizeAndPrintRequestButton"
            v-shortkey="['alt', 'p']"
            qa="study-request-form-formalize-and-print-button"
            class="btn--primary btn-sm ml-2"
            size="sm"
            :params="{ promise: saveFormalizeAndPrintRequest, shortkeys: ['alt', 'p'] }"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />{{ $t('component.studyRequest.form.formalizeAndPrint') }}
          </vt-loading-button>
          <vt-loading-button
            v-if="visibleDownloadCaseApReportPdfButton"
            qa="study-request-form-pathology-report-button"
            class="btn--primary btn-sm ml-2"
            :params="{ promise: downloadCaseAPReportPdf }"
          >
            <i
              class="fal fa-file-contract mr-2"
              aria-hidden="true"
            />{{ $t('global.report') }}
          </vt-loading-button>
        </div>
      </div>
    </b-card>
    <modal-patient-card
      v-if="patientInformation"
      ref="modalPatientCard"
      :patient-id="patientInformation.patId"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :request-created="!!requestId"
        :attachment-entity-type="attachmentEntityType"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded($event)"
        @addAttachmentToNotCreatedRequest="onAddAttachmentToNotCreatedRequest($event)"
      />
    </modal-wrapper>
    <modal-unsaved-changes
      id="studyRequestFormUnsavedChangesModal"
      ref="unsavedChangesModal"
    />
    <print-component ref="printComponent" />
    <clinical-search-patient-modal
      ref="clinicalSearchPatientModal"
      id-modal="formClinicalSearcher"
      @patientSelected="onPatientSelected($event)"
    />
  </div>
</template>

<script>
import patientResource from '@/api/resources/PatientResource'
import sessionStore from '@/session/sessionStore'
import regimeResource from '@/api/resources/RegimeResource'
import clinicalRequestService from '@/modules/clinicalModule/services/clinicalRequestService'
import PatientCard from '@/modules/clinicalModule/components/PatientCard'
import VtBubble from '@/components/common/VtBubble'
import StudyRequestStatusBadge from '@/modules/clinicalModule/components/StudyRequestStatusBadge'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import RequestDownloadableAttachment from '@/modules/clinicalModule/components/RequestDownloadableAttachment'
import RequestIncidentCard from '@/components/IncidentCard'
import commonDepartmentService from '@/api/resources/common/commonDepartmentService'
import commonCenterService from '@/api/resources/common/commonCenterService'
import petitionerResource from '@/api/resources/PetitionerResource'
import SubRequestForm from '@/modules/clinicalModule/views/form/SubRequestForm'
import SearcherModuleEntitySelection from '@/components/SearcherModuleEntitySelection'
import AddFileModal from '@/components/AddFileModal'
import { cloneDeep, isEqual, isEqualWith, pick, uniqueId } from 'lodash'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import SubRequestCustomForm from '@/modules/clinicalModule/views/form/SubRequestAdditionalInformations'
import ModalUnsavedChanges from '@/components/modals/ModalUnsavedChanges'
import ClinicalSearchPatientModal from '@/modules/clinicalModule/components/clinical-search-patient-modal/ClinicalSearchPatientModal'
import PrintComponent from '@/modules/printModule/PrintComponent'
import PrintLabelPopover from '@/modules/clinicalModule/components/Popover/PrintLabelPopover'
import incidentServices from '@/api/resources/incident/incidentServices'
import sampleTypeService from '@/api/resources/sampleTypeService'
import { RequestOperation } from '@/api/operations/models/request-operation'
import apiExceptionType from '@/utils/apiExceptionType'
import { IDENTIFIER_SCOPES, PATIENT_IDENTIFIER } from '@/api/resources/identifierScopesResource'
import CustomConfigResource from '@/api/resources/ui/CustomConfigResource'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import clinicalCasePrintService from '@/modules/printModule/services/ClinicalCasePrintService'
import clinicalRequestPrintService from '@/modules/printModule/services/ClinicalRequestPrintService'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { RequestOperationGuardMixin } from '@/api/operations/mixins/guards/request-operation-guard.mixin'
import { CONFIGURATION_PARAMETERS } from '@/modules/configurationModule/constants/parameter'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { commonInsurerService } from '@/api/services/common/insurer/common-insurer.service'
import { StoreUtils } from '@/shared/util/store-utils'

export default {
    name: 'StudyRequestForm',
    components: {
        ClinicalSearchPatientModal,
        PatientCard,
        VtBubble,
        StudyRequestStatusBadge,
        ModalPatientCard,
        RequestDownloadableAttachment,
        RequestIncidentCard,
        SubRequestForm,
        SubRequestCustomForm,
        AddFileModal,
        SearcherModuleEntitySelection,
        ModalUnsavedChanges,
        PrintComponent,
        PrintLabelPopover,
        AlertBanner
    },
    mixins: [
        CaseOperationGuardMixin,
        RequestOperationGuardMixin
    ],
    beforeRouteLeave(to, from, next) {
        if (this.hasBeenChanged() && this.checkChanges) {
            this.$refs.unsavedChangesModal.askAndExecuteCallbacks(
                () => this.saveRequest(), () => next())
        } else {
            next()
        }
    },
    data() {
        return {
            isRequestExternalLabelValid: false,
            loaded: false,
            printedLabel: null,
            incidents: 0,
            isReassign: false,
            REQUEST_STATES: REQUEST_STATES,
            originalRequest: null,
            showErrors: false,
            subrequestNumberToAdd: 1,
            attachments: [],
            selectedSubrequestIndex: null,
            physicianPet: null,
            patientInformation: null,
            requestId: null,
            labId: null,
            destinationPet: null,
            subRequestSelected: null,
            sessionState: sessionStore.state,
            requestLabel: null,
            notes: {},
            clinicalData: {},
            mostLikelyDiagnosis: [],
            topographicCodes: [],
            procedures: [],
            incidentsList: [],
            request: this.getRequestData(),
            selected: null,
            differentDestination: false,
            toggleSidepanel: false,
            incidentsSidepanel: false,
            attachmentsSidepanel: false,
            patientInformationOriginal: null,
            checkChanges: true,
            uicomponentExternalLabel: 'uicomponent.petitioner.requestFormExternalLabel',
            requestCaseId: null,
            selectedPhysicianCenter: null,
            selectedPhysicianCenterId: null,
            selectedDestinationCenter: null,
            selectedDestinationCenterId: null,
            showBackButton: false
        }
    },
    computed: {
        disabledPrintRequestReport() {
            return !this.request.id
        },
        hasRequestIdAndIsNotInDraft() {
            return this.request.id && !this.$_isRequestDraft(this.request)
        },
        visibleCancelRequestButton() {
            return this.request?.metadata?.operationGuards?.canBeCancelled && this.$_canCancelRequest(this.labId)
        },
        visibleDiscardRequestButton() {
            return this.request?.metadata?.operationGuards?.canBeDeleted && this.$_canCreateRequest(this.labId)
        },
        visibleSaveRequestButton() {
            return (this.requestId ? this.$_isRequestDraft(this.request) : true) &&
                this.$_canCreateRequest(this.labId)
        },
        visibleSaveAndFormalizeRequestButton() {
            return this.visibleSaveRequestButton
        },
        visibleSaveFormalizeAndPrintRequestButton() {
            return this.visibleSaveRequestButton
        },
        visibleDownloadCaseApReportPdfButton() {
            return this.requestId && this.$_isRequestCompleted(this.request)
        },
        subRequestCustomFormKey() {
            return `${this.$options.filters.abc(this.selectedSubrequestIndex)}-customForm`
        },
        subRequestFormKey() {
            return `${this.$options.filters.abc(this.selectedSubrequestIndex)}-form`
        },
        identifierScopesForSearch() {
            return [
                {
                    scopes: [IDENTIFIER_SCOPES.CENTER_PATIENT, IDENTIFIER_SCOPES.PERSON],
                    icon: 'fal fa-user-circle',
                    text: this.$t('component.searcher.patient'),
                    id: PATIENT_IDENTIFIER
                },
                {
                    scopes: [IDENTIFIER_SCOPES.REQUEST],
                    icon: 'fal fa-file-medical',
                    text: this.$tc('global.request', 1),
                    errorMessage: this.$t('clinical.error.validIdentifierRequest')
                },
                {
                    scopes: [IDENTIFIER_SCOPES.CASE],
                    icon: 'fal fa-chart-network',
                    text: this.$tc('global.case', 1),
                    errorMessage: this.$t('clinical.error.validIdentifierCase')
                }
            ]
        },
        entitiesIdArray() {
            return [this.labId, this.requestId]
        },
        attachmentEntityType() {
            return ATTACHMENT_ENTITY_TYPE.REQUEST
        },
        requestRegex() {
            return this.$store.getters.regex.filter((reg) => reg.identifierType.idtScope === IDENTIFIER_SCOPES.REQUEST && reg.laboratory && reg.laboratory.labId === this.request.laboratory.labId)
        },
        subrequestRegex() {
            return this.$store.getters.regex.filter((reg) => reg.identifierType.idtScope === IDENTIFIER_SCOPES.SUBREQUEST && reg.laboratory && reg.laboratory.labId === this.request.laboratory.labId)
        },
        laboratories() {
            return StoreUtils.laboratories.filter(laboratory =>
                this.$store.getters.laboratoriesConfiguration
                    .find(configuration =>
                        laboratory.labId === configuration.laboratory.labId &&
                        this.$_canCreateRequest(laboratory.labId) &&
                        configuration.activeModules.find(module =>
                            !!module.activeStatus &&
                            module.moduleCode === CONFIGURATION_PARAMETERS.ACTIVE_MODULES.CLINICAL_MODULE)))
        },
        priorities() {
            return this.sessionState.priorities
        },
        userInformation() {
            return this.$store.getters.userInformation
        },
        userHaveAttachOperation() {
            if (this.labId) {
                return !!(this.hasLabOperationGlobalMixin(this.labId, RequestOperation.ATTACH))
            }
            return true
        },
        filteredSampleTypes() {
            const subrequests = [].concat(this.request.subrequests)
            subrequests.splice(this.selectedSubrequestIndex, 1)
            return subrequests.map(subrequest => subrequest.sampleType).filter(s => s)
        },
        htmlShowCaseLabelInput() {
            return !!(this.request.caseLabel || this.request.status?.code === REQUEST_STATES.REQUESTED.code)
        },
        htmlDisabledCaseLabelInput() {
            return !!(this.request.status && !this.$_isRequestDraft(this.request))
        }
    },
    async mounted() {
        const patient = this.$route.params.patientId ? await patientResource.findById(this.$route.params.patientId) : this.$route.params.initPatient
        await this.initializeRequestForm(patient, this.$route.params.requestId, this.$route.params.labId)
        this.showBackButton = this.$route.params.showBackButton
        this.$emit('loadSummary')
        this.loaded = true
    },
    methods: {
        async findAllActiveInsurers() {
            const response = await this.$_toCallUnique(commonInsurerService.findAllActiveInsurers())
            return response ?? []
        },
        onSubrequestInformationsChanged(subrequestInformations) {
            this.subRequestSelected.subrequestInformations = cloneDeep(subrequestInformations)
        },
        uniqueId(prefix) {
            return uniqueId(prefix)
        },
        generateSubrequestNumber(subrequestIndex) {
            return this.$options.filters.abc(subrequestIndex)
        },
        async initializeRequestForm(patient, requestId, laboratoryId) {
            if (patient) {
                this.initialRequestData(patient)
            } else if (requestId && laboratoryId) {
                this.requestId = Number(requestId)
                this.labId = Number(laboratoryId)
                await this.loadRequest()
            }
            if (!this.requestId) {
                const isExternalRequestLabelCustomConfig = await CustomConfigResource.get(this.uicomponentExternalLabel)
                if (isExternalRequestLabelCustomConfig?.config) {
                    this.request.isExternalLabel = isExternalRequestLabelCustomConfig.config.isExternalLabel
                }
                this.setDefaultCenterAndDepartment()
            }
        },
        async downloadRequestReportPdf() {
            const blob =
                await this.$_toCallSecure(
                    clinicalRequestPrintService.downloadRequestReportPdf([this.requestId]))
            if (blob) {
                const openFileParams = {
                    blob,
                    newWindowTabTitle: `${this.request.label} - ${this.$tc('global.report')}`
                }
                this.$_openFile(openFileParams)
            }
        },
        async downloadCaseAPReportPdf() {
            const blob =
                await this.$_toCallSecure(
                    clinicalCasePrintService.downloadCaseAPReportPdf(
                        this.labId,
                        this.requestCaseId,
                        false))
            if (blob) {
                const openFileParams = {
                    blob,
                    newWindowTabTitle: `${this.request.caseLabel} - ${this.$tc('global.report')}`
                }
                this.$_openFile(openFileParams)
            }
        },
        async onAddAttachmentToNotCreatedRequest() {
            const createRequestResponse = await this.showConfirmationModalGlobal(this.$t('clinical.form.requestNotCreated'), this.$t('global.save'), this.$t('global.cancel'))
            if (createRequestResponse && await this.saveRequest()) {
                await this.$refs.addFileModal.addAttachmentsCall()
            }
        },
        handleSubrequestTabsNavigation(event) {
            const indexToSelect = event.srcKey === 'left' ? this.selectedSubrequestIndex - 1 : this.selectedSubrequestIndex + 1
            if (this.request.subrequests[indexToSelect]) {
                this.selectSubRequest(this.request.subrequests[indexToSelect], indexToSelect)
            }
        },
        async getRequestIncidents() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(incidentServices.getRequestIncidents(this.request.laboratory.labId, this.requestId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.incidentsList = response
        },
        saveSwitchExternalLabelState() {
            const isExternalRequestLabel = { isExternalLabel: this.request.isExternalLabel }
            CustomConfigResource.save(this.uicomponentExternalLabel, isExternalRequestLabel)
            if (!this.request.isExternalLabel) {
                this.request.label = null
            }
        },
        async cancelRequest() {
            const cancelResponse = await this.showConfirmationModalGlobal()
            if (cancelResponse) {
                this.updateLoading(true)
                const [err] = await this.toCall(clinicalRequestService.batchCancel([this.requestId]))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                this.showSuccessModalGlobal(this.$t('studyRequest.info.requestCanceled'))
                await this.loadRequest()
            }
        },
        async printRequestReport() {
            await this.$refs.printComponent.printRequestReportFromClinical({ id: this.requestId })
        },
        async printLabels(singleSubrequest) {
            let subrequestsListToPrint
            if (singleSubrequest) {
                subrequestsListToPrint = [this.subRequestSelected]
            } else {
                subrequestsListToPrint = this.request.subrequests
            }
            await this.$refs.printComponent.printSubrequestReportFromClinical(
                this.labId,
                this.requestId,
                subrequestsListToPrint
            )
        },
        printSelectedLabelsWithCopies(selectedLabels) {
            this.$refs.printComponent.printSubrequestReportFromClinical(
                this.labId,
                this.requestId,
                selectedLabels
            )
        },
        reassignPatient() {
            this.isReassign = true
            this.$refs.clinicalSearchPatientModal.show(true)
        },
        onDeleteAttachment() {
            this.toggleSidepanel = false
            this.showSuccessModalGlobal(this.$t('clinical.form.attachemntDeletedSuccessfully'))
            this.loadRequest()
        },
        getRequestData() {
            return {
                clinicalData: {},
                notes: {},
                label: null,
                isExternalLabel: false,
                laboratory: null,
                priority: null,
                regime: {},
                reqEpisode: null,
                physicianPetitioner: null,
                physicianDepartment: null,
                destinationDepartment: null,
                originalConcurrencyUpdatedAt: null,
                subrequests: [],
                reqAttn: null,
                checkChanges: true,
                externalReference: '',
                insurer: null
            }
        },
        async onPatientSelected(patId) {
            if (this.isReassign) {
                this.patientInformation = await patientResource.findById(patId)
                this.isReassign = false
            } else {
                this.goToNewRequest(patId)
            }
        },
        goToNewRequest(patId) {
            if (this.hasBeenChanged()) {
                this.$refs.unsavedChangesModal.askAndExecuteCallbacks(
                    () => this.saveRequest(), () => {
                        this.newRequest(patId)
                    })
            } else {
                this.newRequest(patId)
            }
        },
        initialRequestData(patient) {
            this.patientInformation = patient
            this.patientInformationOriginal = cloneDeep(patient)
            if (this.userInformation?.petitioner?.isPhysician) {
                this.request.physicianPetitioner = this.userInformation.petitioner
                this.customLabelProfessional(this.userInformation.petitioner)
            }
            if (!this.request.subrequests.length) {
                this.addSubRequest()
            }
            this.selectSubRequest(this.request.subrequests[0], 0)
            this.originalRequest = cloneDeep(this.request)
            this.differentDestination = false
        },
        setDefaultCenterAndDepartment() {
            const petitionerDepartments = this.userInformation?.petitioner?.departments
            if (petitionerDepartments?.length) {
                this.selectedPhysicianCenter = petitionerDepartments[0].center
                this.request.physicianDepartment = petitionerDepartments[0]
            }
        },
        resetValidator() {
            this.$validator.pause()
            this.$nextTick(() => {
                this.$validator.errors.clear()
                this.$validator.fields.items.forEach(field => field.reset())
                this.$validator.fields.items.forEach(field => this.errors.remove(field))
                this.$validator.resume()
            })
        },
        async newRequest(patId) {
            const patient = await patientResource.findById(patId)
            if (!this.requestId && !this.patientInformation) {
                await this.resetLaboratoryChanges()
                this.subrequestNumberToAdd = 1
                this.showErrors = false
                this.incidents = 0
                this.attachments = []
                this.initialRequestData(patient)
            } else {
                if (this.$router.currentRoute.name === 'newRequest') {
                    this.$_cleanComponentData()
                    this.resetValidator()
                    await this.initializeRequestForm(patient, this.requestId, this.labId)
                    this.loaded = true
                } else {
                    this.$router.push({ name: 'newRequest', params: { initPatient: patient } })
                }
            }
        },
        async removeRequest() {
            const removeRequestResponse = await this.showConfirmationModalGlobal()
            if (removeRequestResponse) {
                const [err] = await this.toCall(clinicalRequestService.batchDiscard([this.requestId]))
                if (err) {
                    this.showErrorModalGlobal(this.$t('studyRequest.error.requestDeletionFailed'))
                } else {
                    this.showSuccessModalGlobal(this.$t('studyRequest.info.requestDiscarded'))
                    this.$emit('loadSummary')
                    this.checkChanges = false
                    this.$router.push({ name: 'clinicalGrid' })
                }
            }
        },
        async patientSearch(searchInformation) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: searchInformation.identifierSearch,
                surname1: searchInformation.patientSurname1Search,
                surname2: searchInformation.patientSurmname2Search,
                middleName: searchInformation.patientMiddlenameSearch,
                name: searchInformation.patientNameSearch,
                centerCode: searchInformation.center,
                identifierType: searchInformation.type
            }
            this.updateLoading(true)
            const [err, response] = await this.toCall(patientResource.paginator(params))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED
                    ? this.$t('component.patient.searcher.tooManySearchResults') : this.$t('component.modalError.title'))
            }
            const patients = response.content
            if (!patients.length && searchInformation.identifierSearch) {
                this.showErrorModalGlobal(this.$t('component.patient.searcher.noResultsWithIdentifier', { identifier: searchInformation.identifierName }))
            } else if (!patients.length) {
                this.showErrorModalGlobal(this.$t('component.patient.searcher.noResults'))
            } else if (patients.length === 1) {
                this.$router.push({ name: 'clinicalPatientDetail', params: { id: patients[0].patId } })
            } else {
                this.$router.push({ name: 'clinicalPatientGrid', params: { patients: patients } })
            }
        },
        async requestSearch(searchInformation) {
            if (this.request.label === searchInformation.trim()) {
                this.$_showWarningModal(this.$tc('registry.requestGrid.search.alreadyLoaded'))
                return
            }
            this.updateLoading(true)
            const [err, response] = await this.toCall(clinicalRequestService.searchRequestByLabel(searchInformation))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND ? this.$t('component.searcher.error.noRequest') : this.$t('component.modalError.title'))
                return
            }
            this.$router.push({
                name: 'editRequest',
                params: {
                    requestId: response.requestId,
                    labId: response.laboratoryId
                }
            })
        },
        async studySearch(searchInformation) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(clinicalRequestService.searchRequestByCaseNumber(searchInformation))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal(err.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND ? this.$t('component.searcher.error.noCase') : this.$t('component.modalError.title'))
                return
            }
            this.$router.push({
                name: 'editRequest',
                params: {
                    requestId: response.requestId,
                    labId: response.laboratoryId
                }
            })
        },
        hasBeenChanged() {
            if (!this.originalRequest) {
                return true
            }
            const originalRequest = this.mapRequestForRequest(this.originalRequest)
            const request = this.mapRequestForRequest(this.request)
            return !isEqualWith(originalRequest, request, (originalRequestObject, requestObjet, key) => {
                return key === 'isExternalLabel' ? true : undefined
            }) || !isEqual(this.patientInformation, this.patientInformationOriginal)
        },
        async saveAndFormalizeRequest() {
            if (await this.saveRequest()) {
                return this.formalizeRequest()
            }
        },
        async saveFormalizeAndPrintRequest() {
            if (await this.saveAndFormalizeRequest()) {
                await this.printRequestReport()
            }
        },
        async onAttachmentsAdded(responseAttachments) {
            this.attachments.push(...responseAttachments)
            await this.saveRequest()
        },
        async openAttachModal() {
            this.$refs.addFileModalWrapper.show()
        },
        selectSubRequest(subrequest, index) {
            this.subRequestSelected = subrequest
            this.selectedSubrequestIndex = index
        },
        deleteSubRequest(deleteAll) {
            if (this.request.subrequests.length <= 1 || deleteAll) {
                this.request.subrequests = [{}]
                this.selectedSubrequestIndex = 0
            } else {
                this.request.subrequests.splice(this.selectedSubrequestIndex, 1)
                if (this.selectedSubrequestIndex >= this.request.subrequests.length) {
                    this.selectedSubrequestIndex = this.request.subrequests.length - 1
                }
            }
            this.subRequestSelected = this.request.subrequests[this.selectedSubrequestIndex]
        },
        async resetLaboratoryChanges() {
            this.$nextTick(() => {
                this.request.subrequests.forEach((subrequest) => {
                    subrequest.topographicCode = null
                    subrequest.sampleType = null
                    subrequest.subrequestInformations = null
                })
                this.sampleTypesSelected = []
                if (this.request.laboratory.labId) {
                    this.labId = this.request.laboratory.labId
                    if (this.request.subrequests.length) {
                        this.$nextTick(async () => {
                            await this.$refs.subRequestForm.loadLaboratoriesCombos()
                            this.$forceUpdate()
                        })
                    }
                } else {
                    this.requestId = null
                    this.requestCaseId = null
                    this.labId = null
                    this.topographicCodes = []
                }
            })
        },
        validateRequestExternalLabelRegex() {
            for (let regex of this.requestRegex) {
                const requestRegexObject = new RegExp('^(?:' + regex.regex + ')$')
                if (requestRegexObject.test(this.request.label)) {
                    return true
                }
            }
            return false
        },
        validateSubrequestExternalLabelRegex() {
            this.request.subrequests.forEach(subrequest => {
                subrequest.isLabelValid = this.subrequestRegex.some(regex => {
                    return new RegExp('^(?:' + regex.regex + ')$').test(subrequest.label)
                })
            })
            return this.request.subrequests.every(subrequest => subrequest.isLabelValid)
        },
        async validateRequest() {
            return this.$validator.validate()
        },
        validateSubrequests() {
            const subrequestsValidation = this.request.subrequests.find((sub) => !sub.topographicCode || !sub.sampleType || (this.request.isExternalLabel && !sub.label))
            return !(subrequestsValidation) || !this.request.subrequests.length
        },
        mapSubrequestInformations(subrequestInformations) {
            if (!subrequestInformations) {
                return []
            }
            return subrequestInformations
                .filter(question => question.possibleAnswer || question.sriAnswer)
                .map(question => {
                    return {
                        formQuestionFoqId: question.formQuestionFoqId || question.formQuestion.foqId,
                        sriAnswer: question.possibleAnswer ? question.possibleAnswer.poaId : question.sriAnswer,
                        possibleAnswerPoaId: question.possibleAnswer && question.possibleAnswer.poaId
                    }
                })
        },
        mapSubrequests(subrequests) {
            let mappedSubrequests = []
            subrequests.forEach((subrequest, index) => {
                let mappedSubrequest = {
                    additionalInformation: subrequest.additionalInformation,
                    id: subrequest.id,
                    label: subrequest.label,
                    mostLikelyMorphologicCodeId: subrequest.mostLikelyMorphologicCode && subrequest.mostLikelyMorphologicCode.id,
                    observations: subrequest.surObservations ? subrequest.surObservations.text : null,
                    procedureId: subrequest.procedure && subrequest.procedure.id,
                    sampleTypeId: subrequest.sampleType && subrequest.sampleType.id,
                    subrequestInformations: this.mapSubrequestInformations(subrequest.subrequestInformations),
                    surConservation: subrequest.surConservation,
                    surExternalId: this.generateSubrequestNumber(index),
                    surFragments: subrequest.surFragments,
                    surSamplingDate: subrequest.surSamplingDate,
                    topographicCodeId: subrequest.topographicCode && subrequest.topographicCode.id
                }
                mappedSubrequests.push(mappedSubrequest)
            })
            return mappedSubrequests
        },
        async saveRequest() {
            this.showErrors = false
            if (!this.requestId || this.hasBeenChanged()) {
                if ((this.selectedPhysicianCenterId && !this.request.physicianDepartment) || (this.selectedDestinationCenterId && !this.request.destinationDepartment)) {
                    this.showErrorModalGlobal(this.$t('studyRequest.create.incompleteFormModal.centerWithoutUnit'))
                    return false
                }
                const isRequestValid = await this.validateRequest()
                const requestValid = isRequestValid && this.validateSubrequests()
                let areSubrequestLabelsValid = true
                if (this.request.isExternalLabel) {
                    this.isRequestExternalLabelValid = this.validateRequestExternalLabelRegex()
                    areSubrequestLabelsValid = this.validateSubrequestExternalLabelRegex()
                }
                if (this.$refs.subRequestForm) {
                    this.$refs.subRequestForm.$forceUpdate()
                }
                if (this.request.isExternalLabel && !this.isRequestExternalLabelValid) {
                    this.showErrors = true
                    this.showErrorModalGlobal(this.$t('clinical.form.requestLabelNotValid'))
                    return false
                }
                if (!requestValid) {
                    this.showErrors = true
                    this.showErrorModalGlobal(this.$t('studyRequest.create.incompleteFormModal.content'))
                    return false
                }
                if (!areSubrequestLabelsValid) {
                    this.showErrors = true
                    this.showErrorModalGlobal(this.$t('clinical.form.someSubrequestLabelNotValid'))
                    return false
                }
                this.updateLoading(true)
                const mappedRequest = this.mapRequestForRequest(this.request)
                if (!this.differentDestination) {
                    mappedRequest.destinationDepartmentId = this.request.physicianDepartment.id
                    mappedRequest.reqAttn = this.destinationPet
                } else {
                    mappedRequest.destinationDepartmentId = this.request.destinationDepartment && this.request.destinationDepartment.id
                }
                if (this.requestId) {
                    const updateRequestResponse = await this.updateRequest(mappedRequest)
                    this.updateLoading(false)
                    if (updateRequestResponse) {
                        this.showToastSuccessGlobal()
                        this.showErrors = false
                        history.pushState(null, '', `/#/requests/laboratory/${this.labId}/request/${this.requestId}`)
                        await this.mapRequestData(updateRequestResponse)
                        await this.initializeRequestForm(null, this.requestId, this.labId)
                    }
                } else {
                    const createSuccess = await this.createRequest(mappedRequest)
                    this.updateLoading(false)
                    if (createSuccess) {
                        this.showToastSuccessGlobal()
                        this.requestId = createSuccess.data.id
                        this.labId = createSuccess.data.laboratory.labId
                        this.showErrors = false
                        await this.mapRequestData(createSuccess.data)
                        this.$emit('loadSummary')
                        history.pushState(null, '', `/#/requests/laboratory/${this.labId}/request/${this.requestId}`)
                        await this.initializeRequestForm(null, this.requestId, this.labId)
                    }
                }
            }
            return true
        },
        async updateRequest(request) {
            const [err, response] = await this.toCall(clinicalRequestService.edit(this.labId, this.requestId, request))
            if (err) {
                this.updateLoading(false)
                if (err.response && err.response.data && err.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.$tc('global.request', 1), err, () => {
                        request.originalConcurrencyUpdatedAt = err.response.data.currentConcurrencyUpdatedAt
                        return this.updateRequest(request)
                    })
                } else if (err.response.data.code === apiExceptionType.UNEXPECTED_REQUEST_CODE) {
                    this.showErrorModalGlobal(this.$t('clinical.form.duplicateRequestLabel'))
                } else {
                    this.showErrorModalGlobalFromErrorResponse(err)
                }
                return false
            }
            return response
        },
        async createRequest(request) {
            const [err, response] = await this.toCall(clinicalRequestService.create(request.laboratoryLabId, request))
            if (err) {
                if (err.response.data.code === apiExceptionType.UNEXPECTED_REQUEST_CODE) {
                    this.showErrorModalGlobal(this.$t('clinical.form.duplicateRequestLabel'))
                    return false
                } else {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return false
                }
            }
            return response
        },
        async formalizeRequest() {
            const [err, response] = await this.toCall(clinicalRequestService.formalize(this.labId, this.requestId))
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            this.showSuccessModalGlobal(this.$t('global.formalizedRequest'))
            await this.mapRequestData(response.data)
            this.$emit('loadSummary')
            await this.initializeRequestForm(null, this.requestId, this.labId)
            return true
        },
        async loadPetitioners(query) {
            if (query) {
                const queryWithoutCommas = query.replace(',', '')
                const paginator = await petitionerResource.paginator({ nameFragments: queryWithoutCommas, size: 1000, active: true })
                return paginator.content
            }
            return []
        },
        async loadRegimes() {
            return regimeResource.lightList()
        },
        async loadDepartments(center) {
            if (center) {
                return commonDepartmentService.findAllActiveByCenter(center.id)
            } else {
                return []
            }
        },
        async loadCenters() {
            return commonCenterService.getActiveCenters()
        },
        customLabelProfessional(data) {
            this.destinationPet = data?.fullName
        },
        addSubRequest() {
            let subRequest = {}
            if (this.$refs.subRequestForm && this.$refs.subRequestForm.internalSelectedFields.length) {
                subRequest = pick(this.subRequestSelected, this.$refs.subRequestForm.internalSelectedFields)
            }
            for (let i = 0; i < this.subrequestNumberToAdd; i++) {
                const createdSubrequest = cloneDeep(subRequest)
                this.request.subrequests.push(createdSubrequest)
            }
            this.$nextTick(() => {
                const indexToSelect = this.request.subrequests.length - 1
                this.selectSubRequest(this.request.subrequests[indexToSelect], indexToSelect)
                this.$forceUpdate()
            })
        },
        mapRequestForRequest(requestData) {
            return {
                additionalInformation: requestData.additionalInformation ? requestData.additionalInformation : null,
                clinicalData: requestData.clinicalData && requestData.clinicalData.text,
                destinationDepartmentId: requestData.destinationDepartment && requestData.destinationDepartment.id,
                isExternalLabel: requestData.isExternalLabel,
                label: requestData.label,
                laboratoryLabId: requestData.laboratory && requestData.laboratory.labId,
                notes: requestData.notes && requestData.notes.text,
                originalConcurrencyUpdatedAt: requestData.originalConcurrencyUpdatedAt,
                patientPatId: this.patientInformation.patId,
                physicianDepartmentId: requestData.physicianDepartment && requestData.physicianDepartment.id,
                physicianPetitionerPetId: requestData.physicianPetitioner && requestData.physicianPetitioner.id,
                priorityId: requestData.priority && requestData.priority.id,
                regimeId: requestData.regime && requestData.regime.id,
                reqAttn: requestData.reqAttn,
                reqEpisode: requestData.reqEpisode,
                subrequests: this.mapSubrequests(requestData.subrequests),
                externalReference: requestData.externalReference,
                insurerId: requestData.insurer?.id,
                referralNumber: requestData.referralNumber,
                insuranceNumber: requestData.insuranceNumber
            }
        },
        mapSubrequestInformationsFromResponse(subrequestInformations) {
            subrequestInformations.forEach(subrequestInformation => {
                const subrequestInformationPossibleAnswers = subrequestInformation.formQuestion.question.possibleanswers
                subrequestInformation.possibleAnswer = subrequestInformationPossibleAnswers.find(possibleAnswer => possibleAnswer.poaId === subrequestInformation.possibleAnswerPoaId)
            })
        },
        async mapRequestData(requestData) {
            this.selectedPhysicianCenter = requestData.physicianDepartment ? requestData.physicianDepartment.center : null
            this.selectedDestinationCenter = requestData.destinationDepartment ? requestData.destinationDepartment.center : null
            this.attachments = requestData.attachments
            this.incidents = requestData.incidents
            this.requestCaseId = requestData.caseId
            this.request = requestData
            this.customLabelProfessional(requestData.physicianPetitioner)
            if (this.request.subrequests.length) {
                const sampleTypeIds = this.request.subrequests.map((subrequest) => subrequest.sampleType.id)
                const [err, sampleTypesWithCaseTypes] = await this.toCall(sampleTypeService.samplesTypeWithRelatedCaseTypesByIds(this.labId, sampleTypeIds))
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                this.request.subrequests = this.request.subrequests.sort((subrequestA, subrequestB) => subrequestA.surExternalId.toUpperCase().localeCompare(subrequestB.surExternalId.toUpperCase()))
                this.selectSubRequest(this.request.subrequests[0], 0)
                this.request.subrequests.forEach(subrequest => {
                    this.mapSubrequestInformationsFromResponse(subrequest.subrequestInformations)
                    subrequest.sampleType = sampleTypesWithCaseTypes.find(sampleType => sampleType.id === subrequest.sampleType.id)
                })
            }
            this.originalRequest = cloneDeep(this.request)
            this.differentDestination = true
            this.$forceUpdate()
        },
        async loadRequest() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(clinicalRequestService.get(this.labId, this.requestId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.patientInformation = response.patient
            this.patientInformationOriginal = cloneDeep(response.patient)
            await this.mapRequestData(response)
        },
        async showIncidentsSidepanel() {
            this.toggleSidepanel = !this.toggleSidepanel
            if (this.toggleSidepanel) {
                await this.getRequestIncidents()
                this.incidentsSidepanel = true
                this.attachmentsSidepanel = false
            }
        },
        showAttachments() {
            this.toggleSidepanel = true
            this.incidentsSidepanel = false
            this.attachmentsSidepanel = true
        },
        async loadPhysicianDepartments() {
            return this.loadDepartments(this.selectedPhysicianCenter)
        },
        async loadDestinationDepartments() {
            return this.loadDepartments(this.selectedDestinationCenter)
        },
        selectPhysicianCenter(selectedCenter) {
            this.request.physicianDepartment = null
            this.selectedPhysicianCenterId = selectedCenter.id
        },
        selectPhysicianDepartment(selectedDepartment) {
            this.request.physicianDepartment = selectedDepartment
        },
        selectDestinationCenter(selectedCenter) {
            this.request.destinationDepartment = null
            this.selectedDestinationCenterId = selectedCenter.id
        },
        selectDestinationDepartment(selectedDepartment) {
            this.request.destinationDepartment = selectedDepartment
        },
        returnToPreviousView() {
            this.$router.go(-1)
        }
    }
}

</script>

<style lang="scss">

/*-- Provisional layout for overflow responsive --*/
.study-request-form .float-component {
    flex-grow: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .card-body {
        flex-grow: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;

        .scrollable-area {
            flex-grow: 1;
            overflow-y: auto;
        }
    }
}

.errorTitle {
    color: red !important;
}

.study-request-form .vt-side-panel .title {
    font-size: font-size(normal);
    color: vt-color(primary);
}

.study-request-form .draft-date small {
    font-size: font-size(very-small) !important;
}

.study-request-form .printed-label {
    border-bottom: 2px solid vt-color(primary);
}

.printed-label-switch-title {
    font-size: font-size(small);
}

.study-request-form .printed-label input::placeholder {
    color: vt-color(base-text);
    font-weight: normal;
}

.study-request-form .printed-label .input-group-prepend .input-group-text {
    border-radius: 14px;
    color: vt-color(search-bar-text);
    border: none;
    background-color: inherit;
    font-size: 0.85rem;
}

.study-request-form .printed-label input {
    width: 180px;
    border: none;
    background-color: inherit;
    color: vt-color(primary);
    font-size: font-size(normal);
    font-weight: bold;
}

.study-request-form .printed-label i {
    font-size: 1rem;
}

.study-request-form .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 20px;
    transition: all 0.3s;
    cursor: pointer;
}

.study-request-form .switch::after {
    content: "";
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 18px;
    background-color: white;
    top: 1px;
    left: 1px;
    transition: all 0.3s;
}

.study-request-form input[type="checkbox"]:checked + .switch::after {
    transform: translateX(20px);
}

.study-request-form input[type="checkbox"]:checked + .switch {
    background-color: vt-color(primary);
}

.study-request-form .offscreen {
    position: absolute;
    left: -9999px;
}

.study-request-form .content-scroll {
    height: calc(100vh - 140px);
    overflow-y: auto;
    overflow-x: hidden;
}

.study-request-form .tab-buttons {
    position: absolute;
    right: 0;
    top: 0;
}

.study-request-form .samples-tabs ul.nav-tabs {
    margin-right: 96px;

    li.nav-item {
        text-align: center;
        min-width: 80px;
    }
}

.study-request-form .card-body {
    padding: 0;
}

.study-request-form .vt-side-panel .card-body {
    padding: 0 1.25rem;
}

.study-request-form .scrollable-area {
    /*height: calc(100vh - 280px);*/
    overflow-y: auto;
    overflow-x: hidden;
}

.study-request-form .secondary-button {
    font-size: 0.9rem;
    font-weight: normal;
}

@media (max-width: 700px) {
    .study-request-form .scrollable-area {
        height: auto !important;
    }
}
</style>
